import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, ChevronDown, Zap, Shield, Bell, Smartphone, BarChart3, Award, ExternalLink, ArrowRight, CheckCircle2, ArrowUp } from 'lucide-react';

const EzTollLanding = () => {
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [isStepsInView, setIsStepsInView] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false); 
  
  const heroRef = useRef(null);
  const aboutRef = useRef(null);
  const featuresRef = useRef(null);
  const stepsRef = useRef(null);
  const teamRef = useRef(null);
  const contactRef = useRef(null);

  const [result, setResult] = useState("");

  const handleNavClick = (e, sectionId) => {
    e.preventDefault(); 
    
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
    setMenuOpen(false); 
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setResult("Sending...");
    const formData = new FormData(event.target);
    formData.append("access_key", "908bcea3-1138-447b-908d-a22bc39de63f");

    const response = await fetch("https://api.web3forms.com/submit", {
      method: "POST",
      body: formData
    });

    const data = await response.json();
    if (data.success) {
      setResult("Message Sent Successfully");
      event.target.reset();
    } else {
      setResult("Error sending message");
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 2500);
    
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setScrolled(scrollPosition > 50);
      
      setShowScrollTop(scrollPosition > 400);

      if (heroRef.current) {
        const heroElements = heroRef.current.querySelectorAll('.parallax-element');
        heroElements.forEach((el, idx) => {
          const speed = (idx + 1) * 0.5;
          el.style.transform = `translateY(${scrollPosition * speed}px)`;
        });
      }

      const sections = [aboutRef, featuresRef, stepsRef, teamRef, contactRef];
      sections.forEach(sectionRef => {
        if (sectionRef.current) {
          const rect = sectionRef.current.getBoundingClientRect();
          const isVisible = rect.top < window.innerHeight * 0.75;
          if (isVisible && !sectionRef.current.classList.contains('animated')) {
            sectionRef.current.classList.add('animated');
            animateSection(sectionRef.current);
          }
        }
      });

      if (stepsRef.current) {
        const rect = stepsRef.current.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.6 && rect.bottom > 0) {
          setIsStepsInView(true);
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    document.documentElement.style.scrollBehavior = 'smooth';
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const animateSection = (section) => {
    const items = section.querySelectorAll('.animate-item');
    items.forEach((item, idx) => {
      setTimeout(() => {
        item.style.opacity = '1';
        item.style.transform = 'translateY(0)';
      }, idx * 100);
    });
  };

  const teamMembers = [
    {
      name: "Sheran Pussadeniya",
      role: "Project Lead & Backend Dev",
      linkedin: "https://www.linkedin.com/in/sheran-pussadeniya-6a2695306",
      image: "/team/sheran.jpeg"
    },
    {
      name: "Nadil Savanjith",
      role: "Backend Dev & UI/UX Engineer",
      linkedin: "https://www.linkedin.com/in/nadil-savanjith-040939320",
      image: "/team/nadil.jpeg"
    },
    {
      name: "Shareefa Kitchilan",
      role: "Mobile App Developer",
      linkedin: "https://www.linkedin.com/in/shareefa-kitchilan",
      image: "/team/shareefa.jpeg"
    },
    {
      name: "Janul Induwara",
      role: "IoT & Hardware Engineer",
      linkedin: "https://www.linkedin.com/in/janul-induwara-7a67a9388/",
      image: "/team/Janul.jpeg"
    },
    {
      name: "Sasiru Manmitha",
      role: "IoT & Hardware Engineer",
      linkedin: "https://www.linkedin.com/in/sasiru-waidyarathna-366404325",
      image: "/team/sasiru.jpeg"
    },
    {
      name: "Hasaral Chandrasinghe",
      role: "Frontend Developer",
      linkedin: "https://www.linkedin.com/in/hasaral-chandrasinghe-8206a0357/?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=android_app",
      image: "/team/hasaral.jpeg"
    }
  ];

  const features = [
    {
      icon: <Zap className="w-8 h-8" />,
      title: "ANPR-Based Detection",
      description: "High-precision cameras capture vehicle plates with 99.9% accuracy"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Secure Payments",
      description: "Automatic deduction from linked cards with bank-grade security"
    },
    {
      icon: <Bell className="w-8 h-8" />,
      title: "Instant Notifications",
      description: "Real-time alerts for tolls, receipts, and balance updates"
    },
    {
      icon: <Smartphone className="w-8 h-8" />,
      title: "Mobile Wallet",
      description: "Track all transactions and manage vehicles from one app"
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Admin Dashboard",
      description: "Real-time monitoring and analytics for highway authorities"
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: "Loyalty Rewards",
      description: "Earn points and get discounts on frequent highway usage"
    }
  ];

  const steps = [
    { step: 1, title: "Vehicle Detection at Entry", desc: "As your vehicle approaches the highway entry point, a high-precision ANPR camera instantly captures the number plate along with key vehicle details. The system automatically logs your entry location, time, and vehicle classification without stopping or slowing down. This ensures fast lane movement and eliminates manual checks completely."},
    { step: 2, title: "Travel on Highway", desc: "Once you enter, the system securely stores your entry record and monitors your journey through the highway network. No interactions or manual toll actions are needed while you drive. The backend keeps the entry data ready for automatic matching when you reach your exit point."},
    { step: 3, title: "Exit Recognition", desc: "At the exit, another ANPR camera captures your number plate and matches it with the entry data. The system calculates the toll fee based on the distance travelled, time of travel, and vehicle type. This all happens within seconds to ensure smooth traffic flow and zero queue buildup."},
    { step: 4, title: "Automatic Payment", desc: "The moment your vehicle is verified at the exit, the system automatically deducts the toll fee from your linked card or wallet. This cashless, automated payment removes the need for top-ups, handling cash, or stopping at counters. You’re charged instantly and securely through a fully integrated backend payment gateway."},
    { step: 5, title: "Receipt Confirmation", desc: "After payment, a digital receipt is immediately added to your mobile app’s transaction history. You also receive a notification confirming the toll charge, distance covered, exit point, and time. Every transaction is stored transparently, giving you full visibility over your past trips and spending."}
  ];

  if (loading) {
      return (
        <div className="fixed inset-0 bg-gray-900 flex items-center justify-center z-50">
          <div className="text-center">
            <div className="relative">
              <div className="w-24 h-24 border-8 border-blue-400 border-t-transparent rounded-full animate-spin mx-auto"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <img 
                 src="/images/Ez-Toll.png"   
                  alt="Loading..."
                  className="w-10 h-10 object-contain animate-pulse" 
                />
              </div>
            </div>
          </div>
        </div>
      );
    }

  return (
    <div className="bg-gray-900 text-white overflow-hidden">
      <style>{`
        .animate-item {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .glass-nav {
          background: rgba(17, 24, 39, 0.7);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .floating-element {
          animation: float 6s ease-in-out infinite;
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        .feature-card {
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          background: linear-gradient(135deg, rgba(31, 41, 55, 0.8) 0%, rgba(17, 24, 39, 0.9) 100%);
        }
        
        .feature-card:hover {
          transform: translateY(-10px) scale(1.02);
          background: linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%);
        }
        
        .scroll-indicator {
          animation: bounce 2s infinite;
        }
        
        .hide-scrollbar::-webkit-scrollbar {
            display: none;
        }
        .hide-scrollbar {
            -ms-overflow-style: none;
            scrollbar-width: none;
        }
        
        @keyframes slowBounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }

        .slow-bounce {
          animation: slowBounce 3s infinite;
        }

        /* --- MOBILE HIDDEN RULE FOR 375x812 and other mobile sizes --- */
        @media only screen and (max-width: 768px) {
          .mobile-hidden-element {
            display: none !important;
          }
        }
      `}</style>

      {/* Floating Background Elements */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-64 h-64 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 floating-element"></div>
        <div className="absolute top-40 right-20 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 floating-element" style={{animationDelay: '2s'}}></div>
        <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-pink-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 floating-element" style={{animationDelay: '4s'}}></div>
      </div>

      {/* Unique Navigation Bar */}
      <nav className={`fixed w-full z-50 transition-all duration-500 ${scrolled ? 'glass-nav py-3' : 'bg-transparent py-5'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">
            
            {/* Logo Section */}
            <div className="flex items-center space-x-3 group cursor-pointer" onClick={scrollToTop}>
              <div className="relative">
                <img 
                  src="/images/Ez-Toll.png"
                  alt="EzToll Logo"
                  className="w-10 h-10 object-contain transform group-hover:scale-110 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-blue-400 blur-xl opacity-0 group-hover:opacity-40 transition-opacity rounded-full"></div>
              </div>
              <span className="text-3xl font-extrabold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">EzToll</span>
            </div>

            <div className="hidden md:flex items-center space-x-1 bg-white/5 backdrop-blur-md rounded-full px-2 py-2 border border-white/10">
              {['About', 'Features', 'How It Works', 'Team', 'Contact'].map((item, idx) => {
                const sectionId = item.toLowerCase().replace(/ /g, '-');
                return (
                  <a 
                    key={idx}
                    href={`#${sectionId}`}
                    onClick={(e) => handleNavClick(e, sectionId)}
                    className="block py-2 px-4 rounded-full hover:bg-white/10 transition-colors no-underline hover:no-underline cursor-pointer"
                  >
                    {item}
                  </a>
                );
              })}
            </div>

            <button className="hidden md:block bg-gradient-to-r from-blue-500 to-purple-500 px-6 py-2 rounded-full text-sm font-semibold hover:shadow-lg hover:shadow-blue-500/50 transition-all duration-300 transform hover:scale-105">
              Download App
            </button>

            <button className="md:hidden" onClick={() => setMenuOpen(!menuOpen)}>
              {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
        
        {menuOpen && (
          <div className="md:hidden glass-nav border-t border-white/10 mt-2">
            <div className="px-4 py-4 space-y-2">
              {['About', 'Features', 'How It Works', 'Team', 'Contact'].map((item, idx) => {
                const sectionId = item.toLowerCase().replace(/ /g, '-');
                return (
                  <a 
                    key={idx}
                    href={`#${sectionId}`}
                    onClick={(e) => handleNavClick(e, sectionId)}
                    className="block py-2 px-4 rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
                  >
                    {item}
                  </a>
                );
              })}
              <button className="w-full bg-gradient-to-r from-blue-500 to-purple-500 px-6 py-2 rounded-full text-sm font-semibold mt-4">
                Download App
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section ref={heroRef} className="relative h-screen flex items-center justify-center overflow-hidden">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover z-0"
        >
          <source src="/videos/highway-video.mp4" type="video/mp4" />
        </video>

        <div className="absolute inset-0 bg-black/60 z-10"></div>
        
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gray-900/30 to-gray-900/60 z-10"></div>
        
        <div className="relative z-20 text-center px-4 max-w-6xl mx-auto parallax-element mt-40">
          {/* APPLIED CLASS: mobile-hidden-element */}
          <div className="mobile-hidden-element inline-block mb-6 px-6 py-2 bg-blue-500/20 backdrop-blur-md rounded-full border border-blue-400/30 animate-pulse">
            <span className="text-blue-300 text-sm font-semibold">🚀 Sri Lanka's First Smart Toll System</span>
          </div>
          
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-6">
            Smart Highway Toll System
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 mt-4">
              EzToll
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto leading-relaxed">
            Experience queue-free, fully automated toll payment solution powered by cutting-edge ANPR technology
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 mb-12 parallax-element">
            {['No Queues', '100% Automated', 'Instant Notifications'].map((text, idx) => (
              <div key={idx} className="flex items-center space-x-2 bg-white/10 backdrop-blur-md px-6 py-3 rounded-full border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:scale-105">
                <CheckCircle2 className="w-5 h-5 text-green-400" />
                <span className="font-medium">{text}</span>
              </div>
            ))}
          </div>

          {/* APPLIED CLASS: mobile-hidden-element */}
          <div className="mobile-hidden-element flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center parallax-element">
            <a href="#how-it-works" onClick={(e) => handleNavClick(e, 'how-it-works')} className="group bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 px-6 py-3 text-sm md:px-10 md:py-4 md:text-lg font-semibold rounded-full transition-all transform hover:scale-105 shadow-2xl hover:shadow-blue-500/50 flex items-center justify-center space-x-2">
              <span>Learn More</span>
              <ArrowRight className="w-4 h-4 md:w-5 md:h-5 group-hover:translate-x-1 transition-transform" />
            </a>
            <button className="bg-white/10 backdrop-blur-md hover:bg-white/20 px-6 py-3 text-sm md:px-10 md:py-4 md:text-lg font-semibold rounded-full transition-all transform hover:scale-105 border border-white/30 hover:border-white/50">
              Download App
            </button>
          </div>

          <div className="mt-16 scroll-indicator">
            <ChevronDown className="w-10 h-10 mx-auto text-blue-400" />
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" ref={aboutRef} className="relative py-12 md:py-32 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10 md:mb-20 animate-item">
            <div className="inline-block mb-4 px-6 py-2 bg-blue-500/10 backdrop-blur-md rounded-full border border-blue-400/20">
              <span className="text-blue-400 text-sm font-semibold">ABOUT EZ-TOLL</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">What is EzToll?</h2>
            <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto">Revolutionary technology for modern highways</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
            <div className="space-y-6 md:space-y-8 animate-item">
              <p className="text-lg text-gray-300 leading-relaxed">
                EzToll is an intelligent toll management system designed to eliminate congestion and outdated manual processes on Sri Lankan expressways.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed">
                Built using ANPR cameras, vehicle detection sensors, automated toll calculation, and a secure mobile wallet, EzToll makes highway travel smoother, faster, and more reliable.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed">
                The system is built on modern digital tolling standards used in countries like Singapore, Malaysia, Italy, and South Korea, adapted specifically for Sri Lanka's highway network.
              </p>
            </div>
            
            <div className="animate-item">
              <div className="relative p-8 rounded-3xl bg-gradient-to-br from-blue-500/10 to-purple-500/10 backdrop-blur-md border border-white/10 hover:border-blue-400/50 transition-all duration-500 transform hover:scale-105">
                <div className="absolute -top-6 -right-6 w-32 h-32 bg-blue-500 rounded-full blur-3xl opacity-20"></div>
                <h3 className="text-3xl font-bold mb-8 flex items-center space-x-3">
                  <Zap className="w-8 h-8 text-blue-400" />
                  <span>Key Advantages</span>
                </h3>
                <ul className="space-y-5">
                  {['No queues', 'No manual payments', 'No top-up centers', '100% automated tolling', 'Instant notifications'].map((item, idx) => (
                    <li key={idx} className="flex items-center space-x-4 group">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                        <CheckCircle2 className="w-5 h-5" />
                      </div>
                      <span className="text-gray-200 text-lg group-hover:text-white transition-colors">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" ref={featuresRef} className="relative py-12 md:py-32 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-20 animate-item">
            <div className="inline-block mb-4 px-6 py-2 bg-purple-500/10 backdrop-blur-md rounded-full border border-purple-400/20">
              <span className="text-purple-400 text-sm font-semibold">FEATURES</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Key Features</h2>
            <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto">Cutting-edge technology for seamless experience</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {features.map((feature, idx) => (
              <div
                key={idx}
                className="feature-card animate-item p-6 md:p-8 rounded-2xl border border-gray-700/50 backdrop-blur-md group"
              >
                <div className="text-blue-400 mb-6 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">{feature.icon}</div>
                <h3 className="text-2xl font-bold mb-4 group-hover:text-blue-400 transition-colors">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive How It Works */}
      <section id="how-it-works" ref={stepsRef} className="relative py-12 md:py-32 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-20 animate-item">
            <div className="inline-block mb-4 px-6 py-2 bg-green-500/10 backdrop-blur-md rounded-full border border-green-400/20">
              <span className="text-green-400 text-sm font-semibold">HOW IT WORKS</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-green-400 to-blue-400 bg-clip-text text-transparent">How EzToll Works</h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">Experience the seamless journey from entry to exit</p>
          </div>

          <div className="flex flex-col lg:grid lg:grid-cols-2 gap-10 md:gap-16 items-start">
            
            {/* Right Side - Image Container */}
            <div className="animate-item lg:sticky lg:top-24 w-full order-1 lg:order-2">
              <div className="relative rounded-3xl overflow-hidden border border-gray-700 bg-gradient-to-br from-gray-800 to-gray-900 shadow-2xl">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 via-purple-500/20 to-pink-500/20 animate-pulse"></div>
                
                <div className="absolute top-4 right-4 md:top-8 md:right-8 text-6xl md:text-[180px] font-bold text-white/5 leading-none pointer-events-none z-10">
                  {activeStep + 1}
                </div>

                <div className="relative aspect-video lg:aspect-[4/3] p-4 md:p-8">
                  <div className="w-full h-full bg-gradient-to-br from-gray-700 to-gray-800 rounded-2xl flex items-center justify-center overflow-hidden relative group border border-white/10">
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-purple-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    
                    {/* Icon Display */}
                    <div className="relative z-10 transition-all duration-700 transform"
                        style={{
                          transform: `scale(${activeStep >= 0 ? 1 : 0.5}) rotate(${activeStep * 8}deg)`,
                          opacity: activeStep >= 0 ? 1 : 0
                        }}>
                      <div className="text-6xl md:text-[140px] filter drop-shadow-2xl">
                        {steps[activeStep].icon}
                      </div>
                    </div>

                    {/* Image Tag */}
                    <img 
                      src={`/images/step-${activeStep + 1}.jpg`} 
                      alt={steps[activeStep].title}
                      className="absolute inset-0 w-full h-full object-cover transition-all duration-700"
                      style={{
                        opacity: activeStep >= 0 ? 1 : 0,
                        transform: `scale(${activeStep >= 0 ? 1 : 1.2})`
                      }}
                    />

                    <div className="absolute top-4 left-4 w-8 h-8 md:w-12 md:h-12 border-t-2 border-l-2 border-blue-400/50 rounded-tl-xl"></div>
                    <div className="absolute bottom-4 right-4 w-8 h-8 md:w-12 md:h-12 border-b-2 border-r-2 border-purple-400/50 rounded-br-xl"></div>
                  </div>
                </div>

                <div className="flex items-center justify-center gap-2 pb-4 md:pb-8">
                  {steps.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveStep(idx)}
                      className={`h-1.5 md:h-2 rounded-full transition-all duration-500 ${
                        activeStep === idx 
                          ? 'w-8 md:w-12 bg-gradient-to-r from-blue-500 to-purple-500' 
                          : 'w-2 bg-gray-600 hover:bg-gray-500 hover:w-4'
                      }`}
                    ></button>
                  ))}
                </div>
              </div>
            </div>

            {/* Left Side - Steps Description */}
            <div className="space-y-8 animate-item w-full order-2 lg:order-1">
              <div className="flex items-center justify-between w-full py-6 px-1 relative">
                
                {/* Line Container */}
                <div className="absolute top-1/2 -translate-y-1/2 left-5 right-5 md:left-8 md:right-8 h-1 -z-20">
                    <div className="absolute inset-0 bg-gray-800 rounded-full"></div>
                    <div className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all duration-500"
                          style={{
                            width: `${(activeStep / (steps.length - 1)) * 100}%`
                          }}>
                    </div>
                </div>

                {steps.map((item, idx) => (
                  <div key={idx} className="flex flex-col items-center relative z-10">
                    <div
                      className={`cursor-pointer w-10 h-10 md:w-16 md:h-16 rounded-full flex items-center justify-center text-sm md:text-xl font-bold transition-all duration-500 transform ${
                        activeStep === idx
                          ? 'bg-gradient-to-br from-blue-500 to-purple-500 shadow-lg shadow-blue-500/50 scale-110 text-white border-2 border-white/20'
                          : 'bg-gray-800 text-gray-500 border-2 border-gray-700 hover:border-gray-500 hover:scale-105'
                      }`}
                      onClick={() => setActiveStep(idx)}
                    >
                      {item.step}
                    </div>
                  </div>
                ))}
              </div>

              {/* Active Step Description Card */}
              <div className="relative min-h-[400px] md:min-h-[auto]">
                {steps.map((item, idx) => (
                  <div
                    key={idx}
                    className={`transition-all duration-500 ${
                      activeStep === idx 
                        ? 'opacity-100 transform translate-y-0 relative z-10' 
                        : 'opacity-0 absolute inset-0 transform translate-y-8 pointer-events-none z-0'
                    }`}
                  >
                    <div className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 backdrop-blur-md p-6 md:p-8 rounded-3xl border border-gray-700 hover:border-blue-500/50 transition-all duration-500">
                      <div className="flex items-center gap-3 mb-6">
                        <span className="px-4 py-1.5 bg-blue-500/20 border border-blue-400/30 rounded-full text-blue-400 text-sm font-semibold">
                          Step {item.step}
                        </span>
                        <div className="h-px flex-1 bg-gradient-to-r from-blue-500/50 to-transparent"></div>
                      </div>

                      <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                        {item.title}
                      </h3>

                      <p className="text-base md:text-lg text-gray-300 leading-relaxed">
                        {item.desc}
                      </p>

                      <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-blue-500/30 rounded-tl-3xl"></div>
                      <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-purple-500/30 rounded-br-3xl"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" ref={teamRef} className="relative py-12 md:py-32 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 md:mb-20 animate-item">
            <div className="inline-block mb-4 px-6 py-2 bg-pink-500/10 backdrop-blur-md rounded-full border border-pink-400/20">
              <span className="text-pink-400 text-sm font-semibold">OUR TEAM</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-pink-400 to-orange-400 bg-clip-text text-transparent">Meet Our Team</h2>
            <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto">The innovators behind EzToll</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-8">
            {teamMembers.map((member, idx) => (
              <div
                key={idx}
                className="animate-item bg-gradient-to-br from-gray-800 to-gray-900 p-3 md:p-8 rounded-2xl md:rounded-3xl border border-gray-700 hover:border-blue-500 transition-all transform hover:scale-105 text-center group flex flex-col items-center"
              >
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-20 h-20 md:w-32 md:h-32 rounded-full mb-3 md:mb-6 object-cover border-2 md:border-4 border-blue-500/30 group-hover:scale-105 transition-all duration-300"
                />
                <h3 className="text-sm md:text-2xl font-bold mb-1 md:mb-2 group-hover:text-blue-400 transition-colors">{member.name}</h3>
                <p className="text-xs md:text-base text-gray-400 mb-3 md:mb-6">{member.role}</p>
                <a 
                  href={member.linkedin} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-1 md:space-x-2 text-blue-400 hover:text-blue-300 transition-colors mt-auto"
                >
                  <ExternalLink className="w-3 h-3 md:w-5 md:h-5" />
                  <span className="text-xs md:text-base">LinkedIn</span>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" ref={contactRef} className="relative py-12 md:py-32 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10 md:mb-20 animate-item">
            <div className="inline-block mb-4 px-6 py-2 bg-orange-500/10 backdrop-blur-md rounded-full border border-orange-400/20">
              <span className="text-orange-400 text-sm font-semibold">GET IN TOUCH</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">Contact Us</h2>
            <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto">Have questions? We'd love to hear from you</p>
          </div>

          <div className="animate-item bg-gradient-to-br from-gray-800 to-gray-900 p-6 md:p-12 rounded-3xl border border-gray-700">
            <form className="space-y-4 md:space-y-6" onSubmit={onSubmit}>
              <input type="hidden" name="subject" value="EzToll Website Contact Form" />

              <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-300">Name</label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full bg-gray-900/50 border border-gray-700 rounded-xl px-4 py-3 focus:outline-none focus:border-blue-500 transition-colors text-base"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-300">Email</label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full bg-gray-900/50 border border-gray-700 rounded-xl px-4 py-3 focus:outline-none focus:border-blue-500 transition-colors text-base"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Subject</label>
                <input
                  type="text"
                  name="message_title"
                  required
                  className="w-full bg-gray-900/50 border border-gray-700 rounded-xl px-4 py-3 focus:outline-none focus:border-blue-500 transition-colors text-base"
                  placeholder="How can we help?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Message</label>
                <textarea
                  name="message"
                  required
                  rows="6"
                  className="w-full bg-gray-900/50 border border-gray-700 rounded-xl px-4 py-3 focus:outline-none focus:border-blue-500 transition-colors resize-none text-base"
                  placeholder="Tell us more..."
                ></textarea>
              </div>

              <button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 px-8 py-4 rounded-xl text-lg font-semibold transition-all transform hover:scale-105 shadow-lg hover:shadow-blue-500/50 mt-4"
              >
                Send Message
              </button>

              {result && (
                <p className="text-center text-gray-300 pt-4">{result}</p>
              )}
            </form>

          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative py-12 md:py-16 px-4 border-t border-gray-800">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 mb-8 md:mb-12 text-left">
            <div className="col-span-2 md:col-span-1 flex flex-col items-center md:items-start text-center md:text-left mb-6 md:mb-0">
              
              <div className="flex items-center space-x-3 mb-6">
                <img 
                  src="/images/Ez-Toll.png" 
                  alt="EzToll Logo"
                  className="w-8 h-8 object-contain"
                />
                <span className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">EzToll</span>
              </div>
              
              <p className="text-gray-400 leading-relaxed">
                Sri Lanka's first smart highway toll system powered by ANPR technology.
              </p>
            </div>
            
            <div className="pl-4 md:pl-0">
              <h4 className="text-lg font-bold mb-4 text-white">Quick Links</h4>
              <ul className="space-y-2">
                {['About', 'Features', 'How It Works', 'Team'].map((item, idx) => {
                  const sectionId = item.toLowerCase().replace(/ /g, '-');
                  return (
                    <li key={idx}>
                      <a 
                        href={`#${sectionId}`}
                        onClick={(e) => handleNavClick(e, sectionId)}
                        className="text-gray-400 hover:text-blue-400 transition-colors text-sm md:text-base cursor-pointer"
                      >
                        {item}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
            
            <div className="pl-4 md:pl-0">
              <h4 className="text-lg font-bold mb-4 text-white">Resources</h4>
              <ul className="space-y-2">
                {['Documentation', 'API', 'Support', 'Privacy Policy'].map((item, idx) => (
                  <li key={idx}>
                    <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors text-sm md:text-base">
                      {item}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="col-span-2 md:col-span-1 text-center md:text-left mt-4 md:mt-0">
              <h4 className="text-lg font-bold mb-4 text-white">Connect</h4>
              <div className="flex justify-center md:justify-start space-x-6 md:space-x-0 md:block md:space-y-2">
                {['Twitter', 'LinkedIn', 'Facebook', 'Instagram'].map((item, idx) => (
                  <div key={idx} className="md:mb-2">
                    <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors text-sm md:text-base">
                      {item}
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <div className="pt-8 border-t border-gray-800 text-center text-gray-400">
            <p>&copy; 2025 EzToll. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* GO TO TOP BUTTON */}
      <button
        onClick={scrollToTop}
        className={`fixed bottom-8 right-8 p-4 rounded-full bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg transition-all duration-500 z-50 hover:scale-110 hover:shadow-blue-500/50 border border-white/20 transform ${
          showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10 pointer-events-none'
        }`}
        aria-label="Scroll to top"
      >
        <ArrowUp className="w-6 h-6 slow-bounce" />
      </button>

    </div>
  );
};

export default EzTollLanding;